import React, { useState, useRef, useEffect } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import ArrowUpwardIcon from "../../assets/images/modal/trend.png";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { numberWithCommas } from "src/utils/helpers";
import { IoGitMerge } from "react-icons/io5";
import { useGeneralPartnersAnalyticsQuery } from "src/store/services/bookingApi";

const GlobalAnalytics = ({ partner_id }: { partner_id: string }) => {
  const [timePeriod, setTimePeriod] = useState("Last 30 Day");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userDetails, setUserDetails] = useState<{ [key: string]: any }>({});
  const { data: userResp, isLoading: isLoadingUser } =
    useGeneralPartnersAnalyticsQuery();

  useEffect(() => {
    const payload: any = userResp?.payload?.data[0];
    if (!payload) return;
    setUserDetails(payload);
    // console.log(payload[0])
    // setPaginationData(userResp?.payload?.pagination);
    // setUserWalletDetails(payload?.wallet || { balance: 0, transactions: [] });
  }, [userResp]);

  useOnClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  // Dummy data
  const analyticsData = {
    totalRevenue: {
      value: userDetails.total_order_amount,
      Name: "TOTAL REVENUE",
      trend: 18.5,
      icon: <AttachMoneyIcon />,
      bgColor: "bg-blue-50",
      iconBg: "bg-blue-500",
      border: "2px solid #BEDBFF",
      context: null,
    },
    facilitationFees: {
      value: userDetails.total_facilitation_fee,
      Name: "FACILITATION FEES",
      percentage: "8% of revenue",
      icon: <AttachMoneyIcon />,
      bgColor: "bg-purple-50",
      iconBg: "bg-purple-500",
      border: "2px solid #E9D4FF",
      context: "8% of revenue",
      trend: null,
    },
    aoCommissions: {
      value: userDetails.total_partner_share,
      Name: "Total Partner Share",
      percentage: "",
      icon: <AttachMoneyIcon />,
      bgColor: "bg-green-50",
      iconBg: "bg-green-500",
      border: "2px solid #A4F4CF",
      context: "",
      trend: null,
    },
    sfmCommissions: {
      value: userDetails.total_shop_floor_manager_commission,
      Name: "SFM COMMISSIONS",
      percentage: "2% of facilitation fees",
      icon: <AttachMoneyIcon />,
      bgColor: "bg-red-50",
      iconBg: "bg-red-500",
      border: "2px solid #FFCCD3",
      context: "2% of facilitation fees",
      trend: null,
    },
    anyworkxCommission: {
      value: userDetails.total_company_commission,
      Name: "ANYWORKX COMMISSION",
      percentage: "96% of facilitation fees",
      icon: <AttachMoneyIcon />,
      bgColor: "bg-purple-50",
      iconBg: "bg-purple-600",
      border: "2px solid #E8D1E8",
      context: "96% of facilitation fees",
      trend: null,
    },
    activePartners: {
      value: userDetails.total_delivery_fee,
      Name: "TOTAL DELIVERY FEE",
      context: "Across all categories",
      icon: <PeopleIcon />,
      bgColor: "bg-yellow-50",
      iconBg: "bg-orange-500",
      border: "2px solid #FEE685",
      trend: null,
    },
  };

  const timePeriods = [
    "Last 7 Days",
    "Last 30 Day",
    "Last 90 Days",
    "Last Year",
  ];

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BarChartIcon className="text-purple-600" />
          </div>
          <h2 className="text-xl font-semibold text-[#3a3541]">
            Global Analytics
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Categories
          </button>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              {timePeriod}
              <KeyboardArrowDownIcon fontSize="small" />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                {timePeriods.map((period) => (
                  <button
                    key={period}
                    onClick={() => {
                      setTimePeriod(period);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      timePeriod === period
                        ? "bg-purple-50 text-purple-600"
                        : "text-gray-700"
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Revenue */}
        {Object.values(analyticsData).map((item, index) => (
          <div
            key={index}
            className={`${item.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md`}
            style={{ border: item.border }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`${item.iconBg} p-2 rounded-full text-white`}>
                {item.icon}
              </div>
              {item.trend && (
                <div className="bg-green-200 flex items-center justify-between rounded-full gap-2 text-green-600 px-2">
                  <img src={ArrowUpwardIcon} alt="" />
                  <p>{item.trend}%</p>
                </div>
              )}
            </div>

            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                {item.Name}
              </p>

              <p className="text-xl font-bold text-[#3a3541]">
                
                {numberWithCommas(Number(Number(item.value).toFixed(2)))}
              </p>
            </div>

            {item.context && (
              <p className="text-xs text-gray-600">{item.context}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GlobalAnalytics;
